import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { userService } from "../../services/userService";

export const CONSENT_KEY = "peak_cookie_prefs";
export const CONSENT_EVENT = "peak_cookie_prefs_changed";
const PRIVACY_VERSION = "1.00";

export interface CookiePrefs {
  essential: true;
  analytics: boolean;
  marketing: boolean;
}

const defaultPrefs: CookiePrefs = { essential: true, analytics: false, marketing: false };

const Toggle = ({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) => (
  <button
    role="switch"
    aria-checked={enabled}
    onClick={() => onChange(!enabled)}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
      enabled ? "bg-neutral" : "bg-base-200"
    }`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 rounded-full bg-base-100 shadow transition-transform ${
        enabled ? "translate-x-[18px]" : "translate-x-[3px]"
      }`}
    />
  </button>
);

const Cookies = () => {
  const { currentUser, userData, authState } = useAuth();
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>(defaultPrefs);

  useEffect(() => {
    // Wait until auth has resolved so we don't briefly flash the banner
    // for logged-in users who already have consent stored in Firestore.
    if (authState.loading) return;

    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) return;

    // Logged-in user: check if they've already consented on another device.
    if (userData?.cookieConsent) {
      const { analytics, marketing } = userData.cookieConsent;
      const syncedPrefs = { essential: true, analytics, marketing } as CookiePrefs;
      localStorage.setItem(CONSENT_KEY, JSON.stringify(syncedPrefs));
      window.dispatchEvent(
        new CustomEvent(CONSENT_EVENT, {
          detail: syncedPrefs,
        })
      );
      return; // Don't show the banner
    }

    // No consent found anywhere — show the banner after a short delay.
    const timer = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(timer);
  }, [authState.loading, userData]);

  const save = async (finalPrefs: CookiePrefs) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(finalPrefs));
    window.dispatchEvent(
      new CustomEvent(CONSENT_EVENT, {
        detail: finalPrefs,
      })
    );

    // Persist to Firestore for logged-in users so consent follows them
    // across devices and survives browser data clears.
    if (currentUser) {
      try {
        await userService.updateUserProfile(currentUser.uid, {
          cookieConsent: {
            ...finalPrefs,
            timestamp: new Date().toISOString(),
            policyVersion: PRIVACY_VERSION,
          },
        });
      } catch {
        // Non-critical — localStorage consent is still valid.
      }
    }

    setVisible(false);
  };

  const handleAcceptAll = () => save({ essential: true, analytics: true, marketing: true });
  const handleRejectAll = () => save({ essential: true, analytics: false, marketing: false });
  const handleSavePrefs = () => save(prefs);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 flex justify-center p-4 animate-slide-up"
      role="dialog"
      aria-label="Cookie preferences"
      aria-live="polite"
    >
      <div className="w-full max-w-2xl bg-base-100 border border-base-200 rounded-xl shadow-lg overflow-hidden">
        {managing ? (
          <div className="px-5 py-4 flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-base-content">Manage cookie preferences</h2>

            {/* Essential */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-base-content">Essential</p>
                <p className="text-xs text-base-content/60 mt-0.5">
                  Required for authentication and core app functionality. Cannot be disabled.
                </p>
              </div>
              <span className="text-xs font-medium text-base-content/50 shrink-0 mt-0.5">Always on</span>
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-base-content">Analytics</p>
                <p className="text-xs text-base-content/60 mt-0.5">
                  Helps us understand how the app is used so we can improve it (Google Analytics).
                </p>
              </div>
              <Toggle
                enabled={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
            </div>

            {/* Marketing */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-base-content">Marketing</p>
                <p className="text-xs text-base-content/60 mt-0.5">
                  Used to show you relevant ads on other platforms based on your visits (retargeting pixels).
                </p>
              </div>
              <Toggle
                enabled={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              />
            </div>

            <div className="flex gap-2 justify-end pt-1">
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-xs font-semibold text-base-content/80 bg-base-200 hover:bg-base-300 rounded-lg transition-colors"
              >
                Reject all
              </button>
              <button
                onClick={handleSavePrefs}
                className="px-4 py-2 text-xs font-semibold text-white bg-neutral hover:bg-neutral rounded-lg transition-colors"
              >
                Save preferences
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <p className="text-sm text-base-content/80 flex-1 leading-relaxed">
              We use cookies for analytics and advertising.{" "}
              <a
                href="https://peak.app/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-base-content font-medium underline underline-offset-2 hover:text-base-content/80 transition-colors"
              >
                Learn more
              </a>
            </p>
            <div className="flex gap-2 shrink-0 self-end sm:self-auto">
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm font-semibold text-base-content/80 bg-base-200 hover:bg-base-300 rounded-lg transition-colors"
              >
                Reject all
              </button>
              <button
                onClick={() => setManaging(true)}
                className="px-4 py-2 text-sm font-semibold text-base-content/80 bg-base-200 hover:bg-base-300 rounded-lg transition-colors"
              >
                Manage
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-semibold text-white bg-neutral hover:bg-neutral rounded-lg transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cookies;
